// Draft cards in a Bailey thread: which campaign each one is, where in the
// thread it shows, and which of the model's own ```beu``` blocks it stands in
// for.
//
// QA (2026-10-04, Marlowe Hair on staging): Bailey drafted four campaigns and
// the thread showed only prose. The server now sends a 'draft' card for every
// campaign draft_campaign really made (built from the tool result, never the
// prose). Three things follow, all here so they can be tested:
//  - a draft revised in a later turn shows once, on the latest turn;
//  - a reopened thread's id-only cards are opened live;
//  - when the model ALSO writes an email/text block for that same draft, the
//    block is not drawn: two cards for one draft, one of them offering "Save
//    as a draft" for something already saved, is how a second draft gets made.

const SHOW_FOR = { email: 'email', sms: 'text' }

/** The campaign a card is a draft of, or null. */
export const campaignOfCard = (card) => {
  if (!card || card.type !== 'draft' || card.kind !== 'campaign') return null
  const id = card.draftId || (card.draft && card.draft.id)
  if (!id) return null
  const type = card.draft && card.draft.type
  return { draftId: String(id), channel: type === 'sms' ? 'sms' : type === 'email' ? 'email' : null }
}

/** The campaign drafts among a message's cards: [{ draftId, channel }]. */
export const campaignDraftsOf = (cards) => (Array.isArray(cards) ? cards : [])
  .map(campaignOfCard)
  .filter(Boolean)

/**
 * key -> the index of the LAST message carrying that draft card. A draft
 * revised two turns later shows there, not twice.
 */
export const draftCardHomes = (messages) => {
  const homes = new Map()
  ;(Array.isArray(messages) ? messages : []).forEach((m, i) => {
    ;((m && m.cards) || []).forEach((c) => {
      if (c && c.type === 'draft' && c.key) homes.set(c.key, i)
    })
  })
  return homes
}

/** Does this card show on message i? Non-draft cards always do. */
export const showsDraftCardAt = (homes, card, i) => {
  if (!card || card.type !== 'draft' || !card.key || !homes || !homes.has(card.key)) return true
  return homes.get(card.key) === i
}

/**
 * Which of a reply's blocks stand for a draft already shown as a card.
 * `parts` is splitReply(text); `drafts` is campaignDraftsOf(cards).
 * Returns a Set of part indices not to draw.
 *
 * A block that names its draft (draftId) pairs with that draft only. One
 * that doesn't pairs with a draft of the same channel from this same reply,
 * one to one, in order: the model showing the text it has just drafted.
 */
export const blocksCoveredByDrafts = (parts, drafts) => {
  const hidden = new Set()
  const list = Array.isArray(drafts) ? drafts.filter(d => d && d.draftId) : []
  if (!list.length || !Array.isArray(parts)) return hidden
  const used = new Set()
  const blocks = parts
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => p && p.kind === 'block' && p.block && (p.block.show === 'email' || p.block.show === 'text'))
  // Named first, so a named block can't lose its draft to an unnamed one
  blocks.forEach(({ p, i }) => {
    const named = p.block.draftId ? String(p.block.draftId) : null
    if (!named) return
    const d = list.find(x => x.draftId === named)
    if (d) { hidden.add(i); used.add(d.draftId) }
  })
  blocks.forEach(({ p, i }) => {
    if (p.block.draftId || hidden.has(i)) return
    const d = list.find(x => !used.has(x.draftId) && x.channel && SHOW_FOR[x.channel] === p.block.show)
    if (d) { hidden.add(i); used.add(d.draftId) }
  })
  return hidden
}

/**
 * A stored transcript as thread messages. Tool rows stay hidden; the draft
 * cards the server attached to a reply come with it, marked to be opened
 * live (they carry only the id).
 */
export const messagesFromTranscript = (rows, stripContext = (s) => s) => (Array.isArray(rows) ? rows : [])
  .filter(r => r && (r.role === 'user' || r.role === 'assistant'))
  .map((r) => {
    const m = { role: r.role === 'user' ? 'me' : 'them', text: r.role === 'user' ? stripContext(r.content || '') : (r.content || '') }
    const cards = r.role === 'assistant' && Array.isArray(r.cards)
      ? r.cards.filter(c => campaignOfCard(c)).map(c => ({ ...c, loading: !c.draft }))
      : []
    if (cards.length) m.cards = cards
    return m
  })
  .filter(m => m.text || (m.cards && m.cards.length))

/** The id-only draft cards in a set of messages that still need opening. */
export const draftsToOpen = (messages) => {
  const seen = new Set()
  const out = []
  ;(Array.isArray(messages) ? messages : []).forEach((m) => {
    ;((m && m.cards) || []).forEach((c) => {
      const d = campaignOfCard(c)
      if (d && !c.draft && !seen.has(c.key)) { seen.add(c.key); out.push({ kind: 'campaign', id: d.draftId }) }
    })
  })
  return out
}
